import { Box, Button, Dialog, Flex, Checkbox, Text, TextField, IconButton, Select, Tooltip, Badge } from '@radix-ui/themes';
import React, { useState, useEffect, useRef } from 'react';
import { MdOutlineEdit, MdAdd, MdDelete } from 'react-icons/md';
import { BiChevronUp, BiChevronDown, BiTime } from 'react-icons/bi';
import { useLocation } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import axiosInstance from '../../config/axiosConfig';
import { createPortal } from 'react-dom';

const AddReminder = ({ initialValues = null, onReminderSaved, showToast }) => {
  const isEditMode = !!initialValues?.reminder?.token;
  const [isOpen, setIsOpen] = useState(false);
  const [reminderTypes, setReminderTypes] = useState([]);
  const hoursInputRef = useRef(null);
  const minutesInputRef = useRef(null);
  const location = new URLSearchParams(useLocation().search);
  const patientId = location.get('patientId');
  const patientToken = location.get('patientToken');
  const bedId = location.get('bedId');

  // Helper function to format date to YYYY-MM-DD
  const formatDateToYYYYMMDD = (dateString) => {
    if (!dateString) return new Date().toISOString().split('T')[0];
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return new Date().toISOString().split('T')[0];
    return date.toISOString().split('T')[0];
  };

  // Validation schema
  const validationSchema = Yup.object({
    reminderType: Yup.string().required('Reminder type is required'),
    time: Yup.string().required('Time is required').matches(/^\d{2}:\d{2}$/, 'Invalid time format'),
    from: Yup.date().required('From date is required').typeError('Invalid date'),
    to: Yup.date().required('To date is required').typeError('Invalid date').min(Yup.ref('from'), 'To date must be after from date'),
    stagedItems: Yup.array().min(1, 'At least one medication or task is required'),
  });

  // Formik setup
  const formik = useFormik({
    initialValues: {
      token: initialValues?.reminder?.token || null,
      reminderType: initialValues?.reminder?.type === 2 ? 'Task' : 'Medication',
      time: initialValues?.reminder?.time?.split(':')?.slice(0, 2).join(':') || '08:00',
      hours: initialValues?.reminder?.time ? (parseInt(initialValues.reminder.time.split(':')[0]) % 12 || 12).toString() : '8',
      minutes: initialValues?.reminder?.time ? initialValues.reminder.time.split(':')[1] : '00',
      period: initialValues?.reminder?.time ? (parseInt(initialValues.reminder.time.split(':')[0]) >= 12 ? 'PM' : 'AM') : 'AM',
      from: initialValues?.reminder?.from ? formatDateToYYYYMMDD(initialValues.reminder.from) : new Date().toISOString().split('T')[0],
      to: initialValues?.reminder?.to ? formatDateToYYYYMMDD(initialValues.reminder.to) : new Date().toISOString().split('T')[0],
      selectedDays: {
        sunday: initialValues?.repetition?.isSunday || false,
        monday: initialValues?.repetition?.isMonday || false,
        tuesday: initialValues?.repetition?.isTuesday || false,
        wednesday: initialValues?.repetition?.isWednesday || false,
        thursday: initialValues?.repetition?.isThursday || false,
        friday: initialValues?.repetition?.isFriday || false,
        saturday: initialValues?.repetition?.isSaturday || false,
      },
      currentMedication: { medication: '', dosage: '', root: '' },
      currentAction: { action: '' },
      stagedItems: initialValues?.details?.map(item => ({
        id: Date.now() + Math.random(),
        medication: item.medication,
        dosage: item.dosage || '',
        root: item.root || '',
        action: initialValues?.reminder?.type === 2 ? item.medication : '',
      })) || [],
      patientId: patientId || initialValues?.reminder?.patientId || null,
      patientToken: patientToken || initialValues?.reminder?.patientToken || null,
      bedId: bedId || initialValues?.reminder?.bedId || null,
    },
    validationSchema,
    enableReinitialize: false,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      setSubmitting(true);
      try {
        const formattedTime = `${values.time}:00`;

        const reminderData = {
          Token: values.token,
          PatientId: values.patientId,
          PatientToken: values.patientToken,
          BedId: values.bedId,
          Time: formattedTime,
          From: values.from,
          To: values.to,
          Type: reminderTypes.find(t => t.display === values.reminderType)?.key || 1,
          IsActive: true,
          IsSunday: values.selectedDays.sunday,
          IsMonday: values.selectedDays.monday,
          IsTuesday: values.selectedDays.tuesday,
          IsWednesday: values.selectedDays.wednesday,
          IsThursday: values.selectedDays.thursday,
          IsFriday: values.selectedDays.friday,
          IsSaturday: values.selectedDays.saturday,
          RepetitionIsActive: true,
          MedicActionDetails: values.stagedItems.map(item => ({
            Medication: values.reminderType === 'Task' ? item.action : item.medication,
            Dosage: values.reminderType === 'Task' ? null : item.dosage,
            Root: values.reminderType === 'Task' ? null : item.root,
            IsActive: true,
            IsDeleted: false,
          })),
        };

        console.log('Sending reminder data:', JSON.stringify(reminderData, null, 2));

        const endpoint = isEditMode ? '/MedicActionReminder/update' : '/MedicActionReminder/add';
        const requestConfig = {
          headers: { 'Content-Type': 'application/json' },
        };
        const response = isEditMode
          ? await axiosInstance.put(endpoint, reminderData, requestConfig)
          : await axiosInstance.post(endpoint, reminderData, requestConfig);

        console.log('API Response:', response.data);

        if (response.data.result) {
          const successMessage = `Reminder ${isEditMode ? 'updated' : 'created'} successfully`;
          if (showToast) showToast(successMessage);
          if (onReminderSaved) await onReminderSaved();
          setIsOpen(false);
          resetForm();
        } else {
          const errorMessage = `Failed to ${isEditMode ? 'update' : 'create'} reminder: ${response.data.errorResponse?.message || 'Unknown error'}`;
          console.error('API Error:', response.data);
          if (showToast) showToast(errorMessage);
        }
      } catch (error) {
        const errorMessage = `Error ${isEditMode ? 'updating' : 'creating'} reminder: ${error.response?.data?.title || error.message}`;
        console.error('Submission error:', {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status,
          headers: error.response?.headers,
        });
        if (showToast) showToast(errorMessage);
      } finally {
        setSubmitting(false);
      }
    },
  });

  const { values, errors, touched, isSubmitting, handleChange, handleBlur, setFieldValue, handleSubmit, resetForm } = formik;

  // Log Formik values for debugging
  useEffect(() => {
    console.log('Current Formik values:', values);
  }, [values]);

  // Fetch reminder types and transform display values if necessary
  useEffect(() => {
    const fetchReminderTypes = async () => {
      try {
        const response = await axiosInstance.get('/MedicActionReminder/reminder-types');
        const transformedTypes = response.data.result.map(type => ({
          ...type,
          display: type.key === 2 ? 'Task' : type.display, // Ensure type 2 displays as "Task"
        }));
        setReminderTypes(transformedTypes);
      } catch (error) {
        console.error('Error fetching reminder types:', error);
        if (showToast) showToast('Error fetching reminder types');
      }
    };
    fetchReminderTypes();
  }, [showToast]);

  // Fetch reminder details for edit mode
  useEffect(() => {
    if (isEditMode && isOpen && values.token) {
      const fetchReminderDetails = async () => {
        try {
          const response = await axiosInstance.get(`/MedicActionReminder/get?token=${values.token}`);
          const reminder = response.data.result[0]?.reminder;
          const repetition = response.data.result[0]?.repetition;
          const details = response.data.result[0]?.details || [];

          if (reminder) {
            setFieldValue('reminderType', reminder.type === 2 ? 'Task' : 'Medication');
            setFieldValue('time', reminder.time.split(':').slice(0, 2).join(':'));
            setFieldValue('hours', (parseInt(reminder.time.split(':')[0]) % 12 || 12).toString());
            setFieldValue('minutes', reminder.time.split(':')[1]);
            setFieldValue('period', parseInt(reminder.time.split(':')[0]) >= 12 ? 'PM' : 'AM');
            setFieldValue('from', formatDateToYYYYMMDD(reminder.from));
            setFieldValue('to', formatDateToYYYYMMDD(reminder.to));
            setFieldValue('patientId', reminder.patientId);
            setFieldValue('patientToken', reminder.patientToken || patientToken);
            setFieldValue('bedId', reminder.bedId || bedId);
            setFieldValue('selectedDays', {
              sunday: repetition?.isSunday || false,
              monday: repetition?.isMonday || false,
              tuesday: repetition?.isTuesday || false,
              wednesday: repetition?.isWednesday || false,
              thursday: repetition?.isThursday || false,
              friday: repetition?.isFriday || false,
              saturday: repetition?.isSaturday || false,
            });
            setFieldValue('stagedItems', details.map(item => ({
              id: Date.now() + Math.random(),
              medication: item.medication,
              dosage: item.dosage || '',
              root: item.root || '',
              action: reminder.type === 2 ? item.medication : '',
            })));
          }
        } catch (error) {
          console.error('Error fetching reminder details:', error);
          if (showToast) showToast('Error fetching reminder details');
        }
      };
      fetchReminderDetails();
    }
  }, [isOpen, isEditMode, values.token, setFieldValue, showToast, patientToken, bedId]);

  // Update time when hours, minutes, or period change
  useEffect(() => {
    if (values.hours && values.minutes) {
      let hourNum = parseInt(values.hours, 10);
      if (values.period === 'PM' && hourNum !== 12) hourNum += 12;
      if (values.period === 'AM' && hourNum === 12) hourNum = 0;
      const formattedHours = hourNum.toString().padStart(2, '0');
      setFieldValue('time', `${formattedHours}:${values.minutes}`);
    } else {
      setFieldValue('time', '');
    }
  }, [values.hours, values.minutes, values.period, setFieldValue]);

  // Validate and update hours (1–12)
  const handleHoursChange = (value) => {
    const numValue = parseInt(value, 10);
    if (value === '' || (Number.isInteger(numValue) && numValue >= 1 && numValue <= 12)) {
      setFieldValue('hours', value);
    }
  };

  // Validate and update minutes (snap to 5-minute intervals)
  const handleMinutesChange = (value) => {
    const numValue = parseInt(value, 10);
    if (value === '' || (Number.isInteger(numValue) && numValue >= 0 && numValue <= 59)) {
      const roundedMinutes = Math.round(numValue / 5) * 5;
      const formattedMinutes = roundedMinutes === 60 ? '00' : roundedMinutes.toString().padStart(2, '0');
      setFieldValue('minutes', formattedMinutes);
    }
  };

  // Increment/decrement hours
  const adjustHours = (delta) => {
    let current = parseInt(values.hours || '12', 10);
    let newHours = current + delta;
    let newPeriod = values.period;

    if (newHours > 12) {
      newHours = 1;
      newPeriod = values.period === 'AM' ? 'PM' : 'AM';
    } else if (newHours < 1) {
      newHours = 12;
      newPeriod = values.period === 'AM' ? 'PM' : 'AM';
    }

    setFieldValue('hours', newHours.toString());
    setFieldValue('period', newPeriod);
  };

  // Increment/decrement minutes
  const adjustMinutes = (delta) => {
    let current = parseInt(values.minutes || '0', 10);
    let newMinutes = current + delta;
    let newHours = parseInt(values.hours || '12', 10);
    let newPeriod = values.period;

    if (newMinutes >= 60) {
      newMinutes = 0;
      newHours += 1;
      if (newHours > 12) {
        newHours = 1;
        newPeriod = values.period === 'AM' ? 'PM' : 'AM';
      } else if (newHours === 12) {
        newPeriod = values.period === 'AM' ? 'PM' : 'AM';
      }
    } else if (newMinutes < 0) {
      newMinutes = 55;
      newHours -= 1;
      if (newHours < 1) {
        newHours = 12;
        newPeriod = values.period === 'AM' ? 'PM' : 'AM';
      } else if (newHours === 11 && values.period === 'PM') {
        newPeriod = 'AM';
      }
    }

    setFieldValue('minutes', newMinutes.toString().padStart(2, '0'));
    setFieldValue('hours', newHours.toString());
    setFieldValue('period', newPeriod);
  };

  const handleSelectAllDays = (checked) => {
    setFieldValue('selectedDays', {
      sunday: checked,
      monday: checked,
      tuesday: checked,
      wednesday: checked,
      thursday: checked,
      friday: checked,
      saturday: checked,
    });
  };

  const handleDayToggle = (day, checked) => {
    setFieldValue(`selectedDays.${day}`, checked);
  };

  const stageItem = () => {
    if (values.reminderType === 'Medication' && values.currentMedication.medication.trim()) {
      setFieldValue('stagedItems', [
        ...values.stagedItems,
        {
          id: Date.now() + Math.random(),
          medication: values.currentMedication.medication,
          dosage: values.currentMedication.dosage,
          root: values.currentMedication.root,
        },
      ]);
      setFieldValue('currentMedication', { medication: '', dosage: '', root: '' });
    } else if (values.reminderType === 'Task' && values.currentAction.action.trim()) {
      setFieldValue('stagedItems', [
        ...values.stagedItems,
        {
          id: Date.now() + Math.random(),
          action: values.currentAction.action,
          medication: values.currentAction.action,
          dosage: '',
          root: '',
        },
      ]);
      setFieldValue('currentAction', { action: '' });
    }
  };

  const removeItem = (id) => {
    setFieldValue('stagedItems', values.stagedItems.filter(item => item.id !== id));
  };

  const dialogContent = isOpen ? (
    <Dialog.Content maxWidth="500px" onInteractOutside={(e) => e.preventDefault()}>
      <Dialog.Title>{isEditMode ? 'Edit Reminder' : 'Add Reminder'}</Dialog.Title>
      <Dialog.Description size="2" mb="4">
        {isEditMode ? 'Modify the reminder details.' : 'Fill in the details for the new reminder.'}
      </Dialog.Description>

      <form onSubmit={handleSubmit}>
        <Flex direction="column" gap="4">
          {/* Reminder Type */}
          <label>
            <Text as="div" size="2" mb="1" weight="bold">Reminder Type</Text>
            <Select.Root
              name="reminderType"
              value={values.reminderType}
              onValueChange={(value) => {
                setFieldValue('reminderType', value);
                setFieldValue('stagedItems', []);
              }}
            >
              <Select.Trigger style={{ width: '100%' }} />
              <Select.Content position="popper">
                <Select.Group>
                  {reminderTypes.map(type => (
                    <Select.Item key={type.key} value={type.display}>
                      {type.display}
                    </Select.Item>
                  ))}
                </Select.Group>
              </Select.Content>
            </Select.Root>
            {errors.reminderType && touched.reminderType && <Text size="1" color="red" mt="1">{errors.reminderType}</Text>}
          </label>

          {/* Time Picker */}
          <label>
            <Text as="div" size="2" mb="1" weight="bold">Time *</Text>
            <Flex gap="2" align="center">
              <Flex align="center" gap="1">
                <TextField.Root
                  name="hours"
                  value={values.hours}
                  onChange={(e) => handleHoursChange(e.target.value)}
                  placeholder="HH"
                  style={{ width: '40px', textAlign: 'center' }}
                  ref={hoursInputRef}
                  required
                />
                <Flex direction="column">
                  <IconButton 
                    type="button" 
                    size="1" 
                    variant="ghost" 
                    onClick={(e) => {
                      e.preventDefault();
                      adjustHours(1);
                    }}
                  >
                    <BiChevronUp />
                  </IconButton>
                  <IconButton 
                    type="button" 
                    size="1" 
                    variant="ghost" 
                    onClick={(e) => {
                      e.preventDefault();
                      adjustHours(-1);
                    }}
                  >
                    <BiChevronDown />
                  </IconButton>
                </Flex>
              </Flex>
              <Text size="3">:</Text>
              <Flex align="center" gap="1">
                <TextField.Root
                  name="minutes"
                  value={values.minutes}
                  onChange={(e) => handleMinutesChange(e.target.value)}
                  placeholder="MM"
                  style={{ width: '40px', textAlign: 'center' }}
                  ref={minutesInputRef}
                  required
                />
                <Flex direction="column">
                  <IconButton 
                    type="button" 
                    size="1" 
                    variant="ghost" 
                    onClick={(e) => {
                      e.preventDefault();
                      adjustMinutes(5);
                    }}
                  >
                    <BiChevronUp />
                  </IconButton>
                  <IconButton 
                    type="button" 
                    size="1" 
                    variant="ghost" 
                    onClick={(e) => {
                      e.preventDefault();
                      adjustMinutes(-5);
                    }}
                  >
                    <BiChevronDown />
                  </IconButton>
                </Flex>
              </Flex>
              <Select.Root
                name="period"
                value={values.period}
                onValueChange={(value) => setFieldValue('period', value)}
                style={{ width: '60px' }}
              >
                <Select.Trigger />
                <Select.Content position="popper">
                  <Select.Item value="AM">AM</Select.Item>
                  <Select.Item value="PM">PM</Select.Item>
                </Select.Content>
              </Select.Root>
            </Flex>
            <Text as="div" size="1" color="gray" mt="1">Select time in 5-minute intervals (e.g., 8:10 AM)</Text>
            {errors.time && touched.time && <Text size="1" color="red" mt="1">{errors.time}</Text>}
          </label>

          {/* Date Range */}
          <Flex gap="3">
            <label className="w-1/2">
              <Text as="div" size="2" mb="1" weight="bold">From date *</Text>
              <TextField.Root
                type="date"
                name="from"
                value={values.from}
                onChange={handleChange}
                onBlur={handleBlur}
                required
              />
              {errors.from && touched.from && <Text size="1" color="red" mt="1">{errors.from}</Text>}
            </label>
            <label className="w-1/2">
              <Text as="div" size="2" mb="1" weight="bold">To date *</Text>
              <TextField.Root
                type="date"
                name="to"
                value={values.to}
                onChange={handleChange}
                onBlur={handleBlur}
                required
              />
              {errors.to && touched.to && <Text size="1" color="red" mt="1">{errors.to}</Text>}
            </label>
          </Flex>

          {/* Day Selection */}
          <Box>
            <Text as="div" size="2" mb="1" weight="bold">Repeat on days</Text>
            <Flex wrap="wrap" gap="4" mt="1">
              <Flex align="center" gap="1">
                <Checkbox
                  checked={Object.values(values.selectedDays).every(v => v)}
                  onCheckedChange={handleSelectAllDays}
                />
                <Text size="2">All</Text>
              </Flex>
              {Object.entries({ sunday: 'Sun', monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed', thursday: 'Thu', friday: 'Fri', saturday: 'Sat' }).map(([day, label]) => (
                <Flex key={day} align="center" gap="1">
                  <Checkbox
                    name={`selectedDays.${day}`}
                    checked={values.selectedDays[day]}
                    onCheckedChange={(checked) => handleDayToggle(day, checked)}
                  />
                  <Text size="2">{label}</Text>
                </Flex>
              ))}
            </Flex>
          </Box>

          {/* Medication/Task Entry */}
          {values.reminderType === 'Medication' ? (
            <Box>
              <Text as="div" size="2" mb="2" weight="bold">Stage Medication</Text>
              <Text as="div" size="1" color="red" mb="2">Stage medications to include in the reminder. Click Save to confirm.</Text>
              {values.stagedItems.length > 0 && (
                <Box mb="4">
                  <Text as="div" size="2" mb="2" weight="bold">Staged Medications ({values.stagedItems.length})</Text>
                  <Box
                    className="border rounded-md p-2 bg-gray-50"
                    style={{ maxHeight: values.stagedItems.length > 3 ? '150px' : 'auto', overflowY: values.stagedItems.length > 3 ? 'auto' : 'visible' }}
                  >
                    {values.stagedItems.map((item, index) => (
                      <Flex key={item.id} align="center" justify="between" py="2" className={index > 0 ? 'border-t border-gray-200' : ''}>
                        <Flex direction="column" gap="1">
                          <Flex align="center" gap="2">
                            <Text weight="bold">{item.medication}</Text>
                            <Tooltip content="This medication is staged and will be saved when you click Save or Update.">
                            </Tooltip>
                          </Flex>
                          <Text size="1" color="gray">
                            {item.dosage && `${item.dosage}${item.root ? ` - ${item.root}` : ''}`}
                            {!item.dosage && item.root && `Root: ${item.root}`}
                          </Text>
                        </Flex>
                        <IconButton size="1" variant="ghost" color="crimson" onClick={() => removeItem(item.id)}>
                          <MdDelete />
                        </IconButton>
                      </Flex>
                    ))}
                  </Box>
                </Box>
              )}
              <Flex direction="column" gap="3" mb="3">
                <label>
                  <Text as="div" size="2" mb="1" weight="bold">Medication *</Text>
                  <TextField.Root
                    name="currentMedication.medication"
                    value={values.currentMedication.medication}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Enter medication name"
                  />
                </label>
                <Flex gap="3">
                  <label className="w-1/2">
                    <Text as="div" size="2" mb="1" weight="bold">Dosage</Text>
                    <TextField.Root
                      name="currentMedication.dosage"
                      value={values.currentMedication.dosage}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Enter dosage"
                    />
                  </label>
                  <label className="w-1/2">
                    <Text as="div" size="2" mb="1" weight="bold">Root</Text>
                    <TextField.Root
                      name="currentMedication.root"
                      value={values.currentMedication.root}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Enter medication root"
                    />
                  </label>
                </Flex>
                <Button onClick={stageItem} disabled={!values.currentMedication.medication.trim()}>
                  <MdAdd /> Stage Medication
                </Button>
              </Flex>
            </Box>
          ) : (
            <Box>
              <Text as="div" size="2" mb="2" weight="bold">Stage Task</Text>
              <Text as="div" size="1" color="red" mb="2">Stage tasks to include in the reminder. Click Save to confirm.</Text>
              {values.stagedItems.length > 0 && (
                <Box mb="4">
                  <Text as="div" size="2" mb="2" weight="bold">Staged Tasks ({values.stagedItems.length})</Text>
                  <Box
                    className="border rounded-md p-2 bg-gray-50"
                    style={{ maxHeight: values.stagedItems.length > 3 ? '150px' : 'auto', overflowY: values.stagedItems.length > 3 ? 'auto' : 'visible' }}
                  >
                    {values.stagedItems.map((item, index) => (
                      <Flex key={item.id} align="center" justify="between" py="2" className={index > 0 ? 'border-t border-gray-200' : ''}>
                        <Flex direction="column" gap="1">
                          <Flex align="center" gap="2">
                            <Text weight="bold">{item.action}</Text>
                            <Tooltip content="This task is staged and will be saved when you click Save or Update.">
                            </Tooltip>
                          </Flex>
                        </Flex>
                        <IconButton size="1" variant="ghost" color="crimson" onClick={() => removeItem(item.id)}>
                          <MdDelete />
                        </IconButton>
                      </Flex>
                    ))}
                  </Box>
                </Box>
              )}
              <Flex direction="column" gap="3" mb="3">
                <label>
                  <Text as="div" size="2" mb="1" weight="bold">Task *</Text>
                  <TextField.Root
                    name="currentAction.action"
                    value={values.currentAction.action}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Enter task"
                  />
                </label>
                <Button onClick={stageItem} disabled={!values.currentAction.action.trim()}>
                  <MdAdd /> Stage Task
                </Button>
              </Flex>
            </Box>
          )}

          {errors.stagedItems && touched.stagedItems && <Text size="1" color="red" mt="1">{errors.stagedItems}</Text>}

          <Flex gap="3" mt="2" justify="end">
            <Button
              variant="soft"
              color="gray"
              onClick={() => setIsOpen(false)}
              disabled={isSubmitting}
              type="button"
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isEditMode ? 'Update' : 'Save'}
            </Button>
          </Flex>
        </Flex>
      </form>
    </Dialog.Content>
  ) : null;

  return (
    <Dialog.Root open={isOpen} onOpenChange={setIsOpen}>
      {isEditMode ? (
        <Dialog.Trigger>
          <IconButton variant="outline" onClick={() => setIsOpen(true)}>
            <MdOutlineEdit width="18" height="18" />
          </IconButton>
        </Dialog.Trigger>
      ) : (
        <Dialog.Trigger>
          <Button onClick={() => setIsOpen(true)}>Add Reminder</Button>
        </Dialog.Trigger>
      )}
      {isOpen && createPortal(dialogContent, document.body)}
    </Dialog.Root>
  );
};

export default AddReminder;
